import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  HostListener,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, startWith } from 'rxjs/operators';

import { AuxIconName } from '@shared/components/icon/icon';
import { IconComponent } from '@shared/components/icon/icon.component';
import { CustomOverlayRef } from '@shared/components/overlay/custom-overlay-ref';

import { NavigationSearchService, SearchAction } from './services/navigation-search.service';

export interface SearchResult {
  icon?: AuxIconName;
  name: string;
  path?: string;
  url: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, IconComponent],
  selector: 'aux-search-modal',
  styleUrl: './search-modal.component.scss',
  template: `
    <div
      class="flex h-[500px] w-[600px] flex-col rounded-[8px] border bg-white shadow-lg"
      data-pendo-id="search-modal"
    >
      <!-- Search Input -->
      <div class="px-[14px] pt-[14px]">
        <div class="relative">
          <aux-icon
            name="Search"
            class="absolute left-[8px] top-1/2 -translate-y-1/2 transform text-aux-gray-dark-100"
            [size]="20"
          />
          <input
            #searchInput
            [formControl]="searchControl"
            type="text"
            placeholder="Search for pages..."
            class="w-full rounded-[4px] border-[#C0C4CB] bg-transparent px-[36px] py-[6px] text-[14px] outline-none focus:border-aux-blue-light-200 focus:outline-none focus:ring-aux-blue-light-200"
            autocomplete="off"
            spellcheck="false"
            data-pendo-id="search-modal-input"
          />
          @if (searchControl.value) {
            <button
              (click)="clearSearch()"
              class="absolute right-[8px] top-1/2 flex -translate-y-1/2 transform text-aux-gray-dark-100 hover:text-aux-gray-darkest"
            >
              <aux-icon name="X" [size]="20" />
            </button>
          }
        </div>
      </div>

      <!-- Results -->
      <div class="flex-1 overflow-y-auto">
        @if (allResults().length > 0 || !searchControl.value || searchControl.value.length === 0) {
          <div class="space-y-[14px] p-[14px]">
            <!-- Recents Section -->
            @if (
              !searchControl.value ||
              searchControl.value.length === 0 ||
              searchResults().recents.length > 0
            ) {
              <div class="space-y-[7px]">
                <div
                  class="flex items-center gap-[7px] rounded-[4px] bg-aux-gray-light px-[4px] py-[6px]"
                >
                  <aux-icon name="Clock" [size]="16" class="text-aux-gray-darkest" />
                  <h3 class="text-[16px] font-semibold leading-[20px] text-aux-gray-darkest">
                    Recents
                  </h3>
                </div>
                <div class="space-y-[7px]">
                  @if (searchResults().recents.length > 0) {
                    @for (
                      result of searchResults().recents;
                      track 'recent-' + result.url + '-' + i;
                      let i = $index
                    ) {
                      <button
                        (click)="selectResult(result)"
                        (keydown.enter)="selectResult(result)"
                        [attr.data-search-index]="i"
                        [attr.data-pendo-id]="'search-modal-recent-item-' + i"
                        [class.bg-gray-100]="selectedIndex() === i"
                        class="group flex w-full items-center justify-between rounded-[4px] border-0 px-[4px] py-[5px] text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                      >
                        <div class="flex min-w-0 flex-1 items-center">
                          @if (result.icon) {
                            <aux-icon
                              [name]="result.icon"
                              class="mr-[6px] flex-shrink-0 text-black"
                              [size]="20"
                            />
                          }
                          <div class="text-[14px] font-medium leading-[20px] text-black">
                            {{ result.name }}
                          </div>
                        </div>
                        @if (result.path) {
                          <div
                            class="ml-[20px] flex-shrink-0 text-right text-[14px] font-normal leading-[20px] text-[#6B7280]"
                          >
                            /{{ result.path }}
                          </div>
                        }
                      </button>
                    }
                  } @else {
                    <div
                      class="px-[4px] py-[5px] text-[14px] font-normal leading-[20px] text-[#6B7280]"
                    >
                      No recent pages to show. Search or select from Pages below.
                    </div>
                  }
                </div>
              </div>
            }

            <!-- Actions Section -->
            @if (searchResults().actions.length > 0) {
              <div class="space-y-[7px]">
                <div
                  class="flex items-center gap-[7px] rounded-[4px] bg-aux-gray-light px-[4px] py-[6px]"
                >
                  <aux-icon name="SettingsBolt" [size]="16" class="text-aux-gray-darkest" />
                  <h3 class="text-[16px] font-semibold leading-[20px] text-aux-gray-darkest">
                    Actions
                  </h3>
                </div>
                <div class="space-y-[7px]">
                  @for (
                    action of searchResults().actions;
                    track 'action-' + action.name + '-' + i;
                    let i = $index
                  ) {
                    <button
                      (click)="selectAction(action)"
                      (keydown.enter)="selectAction(action)"
                      [attr.data-search-index]="getItemIndex(searchResults().recents.length, i)"
                      [attr.data-pendo-id]="'search-modal-action-item-' + i"
                      [class.bg-gray-100]="
                        selectedIndex() === getItemIndex(searchResults().recents.length, i)
                      "
                      class="group flex w-full items-center rounded-[4px] border-0 px-[4px] py-[5px] text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                      @if (action.icon) {
                        <aux-icon
                          [name]="action.icon"
                          class="mr-[6px] flex-shrink-0 text-black"
                          [size]="20"
                        />
                      }
                      <div class="min-w-0 flex-1">
                        <div class="text-[14px] font-medium leading-[20px] text-black">
                          {{ action.name }}
                        </div>
                        @if (action.description) {
                          <div class="text-[14px] font-normal leading-[20px] text-[#6B7280]">
                            {{ action.description }}
                          </div>
                        }
                      </div>
                    </button>
                  }
                </div>
              </div>
            }

            <!-- Pages Section -->
            @if (searchResults().pages.length > 0) {
              <div class="space-y-[7px]">
                <div
                  class="flex items-center gap-[7px] rounded-[4px] bg-aux-gray-light px-[4px] py-[6px]"
                >
                  <aux-icon name="FileDescription" [size]="16" class="text-aux-gray-darkest" />
                  <h3 class="text-[16px] font-semibold leading-[20px] text-aux-gray-darkest">
                    Pages
                  </h3>
                </div>
                <div class="space-y-[7px]">
                  @for (
                    result of searchResults().pages;
                    track 'page-' + result.url + '-' + i;
                    let i = $index
                  ) {
                    <button
                      (click)="selectResult(result)"
                      (keydown.enter)="selectResult(result)"
                      [attr.data-search-index]="
                        getItemIndex(
                          searchResults().recents.length + searchResults().actions.length,
                          i
                        )
                      "
                      [attr.data-pendo-id]="'search-modal-page-item-' + i"
                      [class.bg-gray-100]="
                        selectedIndex() ===
                        getItemIndex(
                          searchResults().recents.length + searchResults().actions.length,
                          i
                        )
                      "
                      class="group flex w-full items-center justify-between rounded-[4px] border-0 px-[4px] py-[5px] text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                    >
                      <div class="flex min-w-0 flex-1 items-center">
                        @if (result.icon) {
                          <aux-icon
                            [name]="result.icon"
                            class="mr-[6px] flex-shrink-0 text-black"
                            [size]="20"
                          />
                        }
                        <div class="text-[14px] font-medium leading-[20px] text-black">
                          {{ result.name }}
                        </div>
                      </div>
                      @if (result.path) {
                        <div
                          class="ml-[20px] flex-shrink-0 text-right text-[14px] font-normal leading-[20px] text-[#6B7280]"
                        >
                          /{{ result.path }}
                        </div>
                      }
                    </button>
                  }
                </div>
              </div>
            }
          </div>
        } @else if (searchControl.value && searchControl.value.length > 0) {
          <!-- No Results -->
          <div class="flex flex-1 items-center justify-center pt-[14px]">
            <div class="px-[4px] py-[15px] text-center text-[14px] leading-[20px] text-[#6B7280]">
              <!-- “No results for ‘Vendor ABC’. Try a different search term.” -->
              &ldquo;No results for &lsquo;{{ searchControl.value }}&rsquo;. Try a different search
              term.&rdquo;
            </div>
          </div>
        }
      </div>

      <!-- Footer -->
      <div
        class="flex flex-shrink-0 items-center justify-between gap-[17px] rounded-b-[8px] border-t bg-white px-[15px] py-[14px]"
      >
        <div class="flex items-center gap-[17px]">
          <span class="flex items-center gap-[7px]">
            <div
              class="flex size-[20px] flex-col items-center justify-center rounded-[4px] border-[0.5px] border-aux-gray-dark bg-white"
            >
              <aux-icon name="ArrowsLeftRight" [size]="16" class="rotate-90 text-[#354052]" />
            </div>
            <span class="text-[12px] font-normal leading-[20px] text-black">Navigate</span>
          </span>
          <span class="flex items-center gap-[7px]">
            <div
              class="flex h-[20px] flex-col items-center justify-center gap-[10px] rounded-[4px] border-[0.5px] border-aux-gray-dark bg-white px-[4px]"
            >
              <span class="text-[10px] font-normal leading-[20px] text-aux-gray-darkest">
                ENTER
              </span>
            </div>
            <span class="text-[12px] font-normal leading-[20px] text-black">Select</span>
          </span>
          <span class="flex items-center gap-[7px]">
            <div
              class="flex h-[20px] flex-col items-center justify-center gap-[10px] rounded-[4px] border-[0.5px] border-aux-gray-dark bg-white px-[4px]"
            >
              <span class="text-[10px] font-normal leading-[20px] text-aux-gray-darkest">ESC</span>
            </div>
            <span class="text-[12px] font-normal leading-[20px] text-black">Close</span>
          </span>
        </div>
        @if (allResults().length > 0) {
          <span class="text-[12px] font-normal leading-[20px] text-black">
            {{ allResults().length }} RESULTS
          </span>
        }
      </div>
    </div>
  `,
})
export class SearchModalComponent implements AfterViewInit {
  private overlayRef = inject(CustomOverlayRef);
  private router = inject(Router);
  private searchService = inject(NavigationSearchService);

  selectedIndex = signal(0);

  private searchQuery = signal('');

  searchResults = computed(() => {
    const query = this.searchQuery();

    return this.searchService.searchCategorized(query);
  });
  // Track all results for keyboard navigation
  allResults = computed(() => {
    const results = this.searchResults();
    const query = this.searchQuery();

    // When no search query, always show at least the actions (even if recents is empty)
    if (!query || query.trim().length === 0) {
      const allItems: Array<SearchAction | SearchResult> = [
        ...results.recents,
        ...results.actions,
        ...results.pages,
      ];

      return allItems;
    }

    // When searching, only show items that match
    const allItems: Array<SearchAction | SearchResult> = [
      ...results.recents,
      ...results.actions,
      ...results.pages,
    ];

    return allItems;
  });

  searchControl = new FormControl('');
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  ngAfterViewInit() {
    // Focus the search input when modal opens
    setTimeout(() => {
      this.searchInput?.nativeElement?.focus();
    }, 100);
  }

  clearSearch = () => {
    this.searchControl.setValue('');
    this.selectedIndex.set(0);
  };

  constructor() {
    // Listen to search input changes
    this.searchControl.valueChanges
      .pipe(startWith(''), debounceTime(200), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe((value) => {
        this.searchQuery.set(value || '');
        this.selectedIndex.set(0); // Reset selection when search changes
      });
  }

  getItemIndex = (categoryStart: number, itemIndex: number): number => {
    return categoryStart + itemIndex;
  };

  @HostListener('keydown', ['$event'])
  handleKeyDown = (event: KeyboardEvent) => {
    const allItems = this.allResults();
    const currentIndex = this.selectedIndex();

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();

        if (currentIndex < allItems.length - 1) {
          this.selectedIndex.set(currentIndex + 1);
          setTimeout(() => this.scrollToSelectedItem(), 0);
        }

        break;
      case 'ArrowUp':
        event.preventDefault();

        if (currentIndex > 0) {
          this.selectedIndex.set(currentIndex - 1);
          setTimeout(() => this.scrollToSelectedItem(), 0);
        }

        break;
      case 'Enter':
        event.preventDefault();

        if (allItems[currentIndex]) {
          this.selectItem(allItems[currentIndex]);
        }

        break;
      case 'Escape':
        event.preventDefault();
        this.overlayRef.close();
        break;
    }
  };

  private scrollToSelectedItem = () => {
    const selectedIndex = this.selectedIndex();
    const selectedElement = document.querySelector(`[data-search-index="${selectedIndex}"]`);

    if (selectedElement) {
      selectedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  };

  selectAction = (action: SearchAction) => {
    this.overlayRef.close();
    action.action();
  };

  selectItem = (item: SearchAction | SearchResult) => {
    if ('url' in item) {
      this.selectResult(item as SearchResult);
    } else {
      this.selectAction(item as SearchAction);
    }
  };

  selectResult = (result: SearchResult) => {
    this.searchService.addToRecents(result);
    this.overlayRef.close();
    void this.router.navigate([result.url]);
  };
}
